import { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useTreeStore } from '../stores/tree.js'
import { api, API_BASE_URL } from '../services/api.js'
import { useAuthStore } from '../stores/auth.js'
import { usePageTitle } from '../hooks/usePageTitle.js'
import FamilyTreeView from '../components/tree/FamilyTreeView.jsx'
import PedigreeView from '../components/tree/PedigreeView.jsx'
import DiscoveryView from '../components/tree/DiscoveryView.jsx'
import DirectoryView from '../components/tree/DirectoryView.jsx'
import NodeMap from '../components/nodemap/NodeMap.jsx'
import LibraryView from '../components/library/LibraryView.jsx'
import PlacesView from '../components/places/PlacesView.jsx'
import WorldsView from '../components/worlds/WorldsView.jsx'
import GeoMap from '../components/geomap/GeoMap.jsx'
import OriginRail from '../components/origin/OriginRail.jsx'
import SharePublicallyPrompt from '../components/mysteries/SharePublicallyPrompt.jsx'
import AddRelationshipModal from '../components/tree/AddRelationshipModal.jsx'
import GedcomImportModal from '../components/tree/GedcomImportModal.jsx'
import Timeline from '../components/tree/Timeline.jsx'
import InviteModal from '../components/tree/InviteModal.jsx'
import PersonPanel from '../components/tree/PersonPanel.jsx'
import TreeEnrichScanModal from '../components/tree/TreeEnrichScanModal.jsx'
import TreeHealthModal from '../components/tree/TreeHealthModal.jsx'

// Order: primary canvas → flat list of everyone → alternate visualisations
// → research tools. Origin AI is no longer a tab — it lives as a
// context-aware right-rail (OriginRail) visible on every view so it
// always knows what you're looking at and follows you across viz changes.
const VIEWS = [
  { id: 'tree',      label: 'Family Tree' },
  { id: 'directory', label: 'Directory' },
  { id: 'pedigree',  label: 'Pedigree' },
  { id: 'timeline',  label: 'Timeline' },
  { id: 'network',   label: 'Network' },
  { id: 'library',   label: 'Library' },
  { id: 'places',    label: 'Places' },
  { id: 'worlds',    label: 'Worlds' },
  { id: 'discover',  label: 'Discovery' },
]

export default function TreePage() {
  const { treeId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { currentTree, persons, relationships, fetchTree, loading, focalPersonId } = useTreeStore()
  const { token, user } = useAuthStore()
  const isOwner = !!(currentTree && user && currentTree.owner_id === user.id)
  const [mysteriesEnabled, setMysteriesEnabled] = useState(!!currentTree?.public_mysteries_enabled)
  const [mysteriesSaving, setMysteriesSaving] = useState(false)
  useEffect(() => { setMysteriesEnabled(!!currentTree?.public_mysteries_enabled) }, [currentTree?.public_mysteries_enabled])
  async function toggleMysteries() {
    if (mysteriesSaving) return
    setMysteriesSaving(true)
    const next = !mysteriesEnabled
    setMysteriesEnabled(next)  // optimistic
    try {
      await api.patch(`/mysteries/tree/${treeId}`, { enabled: next })
    } catch (err) {
      setMysteriesEnabled(!next)
      alert(err?.message || 'Could not update mysteries setting')
    } finally {
      setMysteriesSaving(false)
    }
  }
  // ?view=library or ?view=places from the Dashboard deep-link; fall back
  // to the tree view when no / unknown value is present.
  const initialView = VIEWS.some(v => v.id === searchParams.get('view'))
    ? searchParams.get('view')
    : 'tree'
  const [activeView, setActiveView] = useState(initialView)
  const [originCollapsed, setOriginCollapsed] = useState(true)
  const [addingRelationship, setAddingRelationship] = useState(false)
  const [importingGedcom, setImportingGedcom] = useState(false)
  const [enrichingTree, setEnrichingTree] = useState(false)
  const [checkingHealth, setCheckingHealth] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [selectedPersonId, setSelectedPersonId] = useState(null)
  const [renamingTree, setRenamingTree] = useState(false)
  const [renameValue, setRenameValue] = useState('')

  usePageTitle(currentTree?.name || 'Tree')
  useEffect(() => { fetchTree(treeId) }, [treeId])

  // Refetch when the tab regains focus, so an approval / edit made elsewhere
  // (e.g. the Suggestions inbox in another tab) is reflected without a manual
  // reload.
  useEffect(() => {
    const onFocus = () => fetchTree(treeId)
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [treeId])

  // Close panel when switching away from views that pair with it
  useEffect(() => {
    if (!['tree', 'pedigree', 'timeline', 'network', 'directory'].includes(activeView)) {
      setSelectedPersonId(null)
    }
  }, [activeView])

  function handlePersonClick(id) {
    setSelectedPersonId(prev => prev === id ? null : id) // toggle off if same
  }

  async function handleRename(e) {
    e.preventDefault()
    if (!renameValue.trim()) return
    await api.patch(`/trees/${treeId}`, { name: renameValue.trim() })
    fetchTree(treeId)
    setRenamingTree(false)
  }

  async function handleExport() {
    const res = await fetch(`${API_BASE_URL}/trees/${treeId}/export/gedcom`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${(currentTree?.name || 'tree').replace(/[^\w -]/g, '')}.ged`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (loading) return <div className="loading">Loading tree</div>

  // Toolbar icons — consistent 15px Lucide-style strokes, themed via currentColor.
  const TI = {
    invite:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/></svg>,
    import:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg>,
    export:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8l-5-5-5 5"/><path d="M12 3v12"/></svg>,
    connect: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/></svg>,
    enrich:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.7 4.3L18 9l-4.3 1.7L12 15l-1.7-4.3L6 9l4.3-1.7z"/></svg>,
    check:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h4l2 5 4-12 2 7h6"/></svg>,
    add:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>,
  }

  const panelOpen = !!selectedPersonId

  return (
    <div className={`tree-page ${originCollapsed ? '' : 'tree-page--origin-open'}`}>
      <div className="tree-page__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={() => navigate('/')}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.85rem' }}
          >
            ← Trees
          </button>
          {renamingTree ? (
            <form onSubmit={handleRename} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                className="form-input"
                value={renameValue}
                onChange={e => setRenameValue(e.target.value)}
                autoFocus
                style={{ fontSize: '1rem', padding: '0.3rem 0.6rem', minWidth: '200px' }}
              />
              <button type="submit" className="btn btn-primary btn-sm">Save</button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRenamingTree(false)}
                style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}>Cancel</button>
            </form>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <h1 className="tree-page__title">{currentTree?.name}</h1>
              <button
                onClick={() => { setRenameValue(currentTree?.name || ''); setRenamingTree(true) }}
                title="Rename tree"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
                  fontSize: '0.85rem', padding: '0.2rem 0.3rem', lineHeight: 1 }}
              >✎</button>
            </div>
          )}
          <span className="tree-page__count">{persons.length} persons</span>
          {isOwner && (
            <button
              onClick={toggleMysteries}
              disabled={mysteriesSaving}
              title={mysteriesEnabled
                ? 'Public Mysteries enabled — Origin AI research leads from this tree are visible on /mysteries. Click to disable.'
                : 'Enable Public Mysteries — share Origin AI research leads from this tree with other researchers. Only historical persons surface. No living-relative data leaks.'}
              style={{
                fontSize: '0.74rem', padding: '0.3rem 0.7rem', marginLeft: '0.6rem',
                background: mysteriesEnabled ? 'rgba(200,165,110,0.16)' : '#fff',
                color: mysteriesEnabled ? '#a8843e' : 'var(--text-muted)',
                border: `1px solid ${mysteriesEnabled ? 'rgba(200,165,110,0.45)' : 'var(--border)'}`,
                borderRadius: 9, cursor: 'pointer', fontWeight: 600, letterSpacing: '0.04em',
                boxShadow: '0 1px 2px rgba(60, 50, 30, 0.05)',
              }}
            >🔎 Mysteries: {mysteriesEnabled ? 'On' : 'Off'}</button>
          )}
        </div>
        <div className="tree-toolbar">
          <button className="tree-toolbar__btn" onClick={() => setInviting(true)} title="Invite a collaborator">
            {TI.invite}<span>Invite</span>
          </button>
          <button className="tree-toolbar__btn" onClick={() => setImportingGedcom(true)} title="Import a GEDCOM file">
            {TI.import}<span>Import</span>
          </button>
          {persons.length > 0 && (
            <button className="tree-toolbar__btn" onClick={handleExport} title="Export as GEDCOM">
              {TI.export}<span>Export</span>
            </button>
          )}

          {(persons.length > 1 || (isOwner && persons.length > 0)) && <span className="tree-toolbar__divider" />}

          {persons.length > 1 && (
            <button className="tree-toolbar__btn" onClick={() => setAddingRelationship(true)} title="Connect existing people">
              {TI.connect}<span>Connect</span>
            </button>
          )}
          {isOwner && persons.length > 0 && (
            <button className="tree-toolbar__btn tree-toolbar__btn--accent" onClick={() => setEnrichingTree(true)}
              title="Enrich this tree from the Knowledge Graph">
              {TI.enrich}<span>Enrich</span>
            </button>
          )}
          {isOwner && persons.length > 1 && (
            <button className="tree-toolbar__btn" onClick={() => setCheckingHealth(true)} title="Check tree health">
              {TI.check}<span>Check</span>
            </button>
          )}
          <button className="tree-toolbar__btn tree-toolbar__btn--primary"
            onClick={() => navigate(`/tree/${treeId}/add-person`)} title="Add a new person">
            {TI.add}<span>Add person</span>
          </button>
        </div>
      </div>

      <SharePublicallyPrompt treeId={treeId} isOwner={isOwner} />

      <div className="view-tabs">
        {VIEWS.map(v => (
          <button key={v.id} className={`view-tab ${activeView === v.id ? 'active' : ''}`}
            onClick={() => setActiveView(v.id)}>
            {v.label}
          </button>
        ))}
      </div>

      {/* Split canvas: view on left, person panel on right */}
      <div className="tree-page__canvas">
        <div className="tree-page__view">
          {activeView === 'tree' && (
            persons.length === 0
              ? <div className="empty-state">
                  <div className="empty-state__icon">🌳</div>
                  <h3>No persons yet</h3>
                  <p>Add your first ancestor to start building the tree.</p>
                  <button className="btn btn-primary" onClick={() => navigate(`/tree/${treeId}/add-person`)}>Add first person</button>
                </div>
              : <FamilyTreeView
                  persons={persons}
                  relationships={relationships}
                  onPersonClick={handlePersonClick}
                  onAddRelated={(personId, relation) =>
                    navigate(`/tree/${treeId}/add-person?relate_to=${personId}&relation=${relation}`)
                  }
                />
          )}
          {activeView === 'pedigree' && (
            persons.length === 0
              ? <div className="empty-state"><div className="empty-state__icon">🌱</div><h3>No persons yet</h3><p>Add your first ancestor to start a pedigree.</p></div>
              : <PedigreeView
                  persons={persons}
                  relationships={relationships}
                  onPersonClick={handlePersonClick}
                />
          )}
          {activeView === 'timeline' && (
            <Timeline
              persons={persons}
              relationships={relationships}
              focalPersonId={focalPersonId}
              onPersonClick={handlePersonClick}
            />
          )}
          {activeView === 'directory' && (
            <DirectoryView onPersonClick={handlePersonClick} />
          )}
          {activeView === 'discover' && (
            <DiscoveryView onPersonClick={handlePersonClick} />
          )}
          {activeView === 'network' && (
            persons.length === 0
              ? <div className="empty-state"><div className="empty-state__icon">🕸️</div><h3>No persons yet</h3><p>Add people to your tree first.</p></div>
              : <NodeMap persons={persons} relationships={relationships} focalPersonId={focalPersonId} onPersonClick={handlePersonClick} />
          )}
          {activeView === 'library' && (
            <LibraryView
              treeId={treeId}
              onOpenDocument={({ personId }) => navigate(`/tree/${treeId}/person/${personId}`)}
            />
          )}
          {activeView === 'places' && <PlacesView treeId={treeId} />}
          {activeView === 'worlds' && <WorldsView treeId={treeId} />}
        </div>

        {/* Person panel — slides in when a node is selected */}
        {panelOpen && (
          <PersonPanel
            key={selectedPersonId}
            personId={selectedPersonId}
            treeId={treeId}
            tree={currentTree}
            onClose={() => setSelectedPersonId(null)}
          />
        )}

        {/* Origin AI — context-aware right rail. Always-on (collapsible),
            follows the user across view + focal-person changes.
            `collapsed` is lifted up so .has-origin-rail padding can
            disappear in sync with the panel — otherwise the PersonPanel
            sits in the middle with a 360px cream gap on the right. */}
        <OriginRail
          treeId={treeId}
          focalPerson={persons?.find(p => p.id === (selectedPersonId || focalPersonId)) || null}
          activeView={activeView}
          collapsed={originCollapsed}
          onCollapsedChange={setOriginCollapsed}
        />
      </div>

      {inviting && (
        <InviteModal treeId={treeId} onClose={() => setInviting(false)} />
      )}
      {enrichingTree && (
        <TreeEnrichScanModal
          treeId={treeId}
          onClose={() => setEnrichingTree(false)}
          onApplied={() => fetchTree(treeId)}
        />
      )}
      {checkingHealth && (
        <TreeHealthModal
          treeId={treeId}
          onClose={() => setCheckingHealth(false)}
          onApplied={() => fetchTree(treeId)}
        />
      )}
      {importingGedcom && (
        <GedcomImportModal
          treeId={treeId}
          onClose={() => { fetchTree(treeId); setImportingGedcom(false) }}
        />
      )}
      {addingRelationship && (
        <AddRelationshipModal
          treeId={treeId}
          persons={persons}
          onSaved={() => fetchTree(treeId)}
          onClose={() => setAddingRelationship(false)}
        />
      )}
    </div>
  )
}
